import { useEffect, useCallback } from "react";
import { useBlocker } from "react-router-dom";
import {
  useExitWarningContext,
  useExitWarningDispatchContext,
  ReducerActionKind,
} from "components/modals/exit-warning/exit-warning.context";

/**
 * Blocks in-app navigation and browser unload while any form is marked dirty
 * via exit-warning context. Clears dirty state when this hook's consumer unmounts.
 */
export const useExitWarningNavigationBlock = () => {
  const dirtyForms = useExitWarningContext();

  const dispatch = useExitWarningDispatchContext();
  const shouldBlockNavigation = dirtyForms.length > 0;

  const onBeforeUnload = useCallback(
    (ev: Event) => {
      if (shouldBlockNavigation) {
        ev.preventDefault();
      }
      return true;
    },
    [shouldBlockNavigation]
  );

  useEffect(() => {
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [onBeforeUnload]);

  const { state, proceed, reset } = useBlocker(shouldBlockNavigation);

  useEffect(
    () => () => {
      dispatch({ type: ReducerActionKind.removeAll });
    },
    [dispatch]
  );

  const handleProceed = useCallback(() => {
    dispatch({ type: ReducerActionKind.removeAll });
    proceed?.();
  }, [dispatch, proceed]);

  return {
    state,
    proceed: handleProceed,
    reset,
  };
};
